import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Icons,
  useTheme,
  useMediaQuery,
} from '@ublend-npm/aulaui-core';
import push from '../../../utils/routing';
import { useAllSpaces } from '../../../hooks/useAllSpaces';
import SpacesList from './SpacesList';
import WidgetCard from '../Home/widgets/common/WidgetCard';

interface AllSpacesPageProps {
  onClose?: () => void;
}

const AllSpacesPage: React.FC<AllSpacesPageProps> = ({ onClose }) => {
  // Simple extraction of the filter query param
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const urlParams = new URLSearchParams(search);
  const filterType =
    (urlParams.get('filter') as 'all' | 'starred' | 'recent') || 'all';

  const allSpacesData = useAllSpaces(filterType);

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      push('/home');
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Auto-redirect if it's the starred view and the user unstars the last space
  useEffect(() => {
    if (
      filterType === 'starred' &&
      allSpacesData.spaces.length === 0 &&
      !allSpacesData.isLoading
    ) {
      handleBack();
    }
  }, [filterType, allSpacesData.spaces.length, allSpacesData.isLoading]);

  // Redirect to home modal view if resizing from mobile to desktop while on this standalone route
  useEffect(() => {
    if (!isMobile && !onClose) {
      push(`/home?openModal=${filterType}`);
    }
  }, [isMobile, onClose, filterType]);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', md: '800px' },
        margin: '0 auto',
        p: { xs: 2, md: 4 },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        mb={3}
        onClick={handleBack}
        sx={{
          cursor: 'pointer',
          width: 'fit-content',
          '&:hover': { opacity: 0.8 },
        }}
        role="button"
        tabIndex={0}
        aria-label="Go back to Home"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleBack();
          }
        }}
      >
        <IconButton
          size="small"
          component="div"
          sx={{ p: 0, mr: 1, pointerEvents: 'none' }}
        >
          <Icons.ArrowBackIos sx={{ width: 16, height: 16 }} />
        </IconButton>
        <Typography variant="body1" fontWeight="medium">
          Back
        </Typography>
      </Box>

      <WidgetCard
        title={
          filterType === 'starred'
            ? 'Starred spaces'
            : filterType === 'recent'
            ? 'Recently viewed'
            : 'All spaces'
        }
        minHeight="auto"
      >
        <SpacesList
          {...allSpacesData}
          hideEmptyState={filterType === 'starred'}
        />
      </WidgetCard>
    </Box>
  );
};

export default AllSpacesPage;
